/**
 * Privacy Policy page - publicly accessible legal document.
 * Required for Azure Communication Services regulatory compliance.
 */

import { makeStyles, tokens, Text } from '@fluentui/react-components';

const useStyles = makeStyles({
    wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        fontFamily: tokens.fontFamilyBase,
        lineHeight: '1.6',
        color: tokens.colorNeutralForeground1,
    },
    title: {
        fontSize: tokens.fontSizeHero800,
        fontWeight: tokens.fontWeightBold,
        marginBottom: '8px',
    },
    lastUpdated: {
        color: tokens.colorNeutralForeground3,
        marginBottom: '32px',
    },
    section: {
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '12px',
        marginTop: '24px',
    },
    paragraph: {
        marginBottom: '12px',
    },
    list: {
        marginLeft: '24px',
        marginBottom: '12px',
    },
    listItem: {
        marginBottom: '8px',
    },
    contact: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

export function PrivacyPage(): JSX.Element {
    const styles = useStyles();

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <h1 className={styles.title}>Privacy Policy</h1>
            <Text className={styles.lastUpdated}>Last updated: December 2, 2024</Text>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Introduction</h2>
                <p className={styles.paragraph}>
                    MacGregor Consulting LLC (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy.
                    This Privacy Policy applies to IRPilot and governs how we collect, use, disclose, and
                    safeguard your information when you use our service. By using this service, you explicitly
                    agree to the terms and conditions of the then-current Privacy Policy in effect at the time
                    of your use.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Information We Collect</h2>

                <h3 className={styles.sectionTitle}>Information You Provide to Us</h3>
                <p className={styles.paragraph}>
                    Our service collects information that is voluntarily provided by authorized administrators
                    when using IRPilot. This information may include:
                </p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>User names and display names</li>
                    <li className={styles.listItem}>Email addresses</li>
                    <li className={styles.listItem}>Phone numbers (for SMS credential delivery)</li>
                    <li className={styles.listItem}>Job titles and department information</li>
                    <li className={styles.listItem}>Organization and tenant identifiers</li>
                </ul>
                <p className={styles.paragraph}>
                    We use this information primarily to provide IT administration services, deliver security
                    credentials, and operate our platform.
                </p>

                <h3 className={styles.sectionTitle}>Information Collected Automatically</h3>
                <p className={styles.paragraph}>
                    We may collect and log IP addresses and other information provided by your web browser
                    about your device and usage patterns when using our service. This information is retained
                    for security and compliance purposes.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>SMS/Text Messaging</h2>
                <p className={styles.paragraph}>
                    When using IRPilot, authorized IT administrators may provide employee phone numbers for
                    the purpose of delivering secure, one-time authentication credentials (Temporary Access Pass).
                </p>
                <p className={styles.paragraph}>
                    <strong>What we send:</strong> Transactional security messages containing one-time login
                    credentials. These are not marketing messages.
                </p>
                <p className={styles.paragraph}>
                    <strong>Frequency:</strong> Messages are sent only when an administrator initiates user
                    provisioning or credential delivery. Typical users receive 1-2 messages total during
                    account setup or credential reset.
                </p>
                <p className={styles.paragraph}>
                    <strong>Opt-out:</strong> Recipients may reply STOP to any message to opt out of future
                    SMS communications. Administrators can also remove phone numbers from the system.
                </p>
                <p className={styles.paragraph}>
                    <strong>Data retention:</strong> Phone numbers are stored only as long as the user account
                    remains active and are deleted when the account is removed.
                </p>
                <p className={styles.paragraph}>
                    <strong>Carrier charges:</strong> Standard message and data rates may apply.
                </p>
                <p className={styles.paragraph}>
                    <strong>No sharing:</strong> We do not sell, rent, or share phone numbers with third
                    parties for marketing purposes.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>How We Use Your Information</h2>
                <p className={styles.paragraph}>
                    By using IRPilot, you explicitly agree to our collection, use, and sharing of information
                    as described in this Privacy Policy. We may use information for the following purposes:
                </p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>Provide IT administration and user management services</li>
                    <li className={styles.listItem}>Deliver one-time security credentials via SMS</li>
                    <li className={styles.listItem}>Respond to inquiries and provide customer support</li>
                    <li className={styles.listItem}>Comply with legal obligations</li>
                    <li className={styles.listItem}>Protect against fraud and security threats</li>
                    <li className={styles.listItem}>Improve our services</li>
                </ul>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Use of Cookies and Tracking Technologies</h2>
                <p className={styles.paragraph}>
                    Our service uses cookies and similar technologies to help personalize your experience
                    and assist with authentication. A cookie is a text file placed on your device by your
                    web browser. You may configure your browser to decline cookies, but this may affect
                    your ability to use certain features.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Disclosing Information to Third Parties</h2>
                <p className={styles.paragraph}>
                    We do not sell personal information to third parties. We may disclose information:
                </p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>When you request or authorize it</li>
                    <li className={styles.listItem}>To comply with law, regulations, court orders, or subpoenas</li>
                    <li className={styles.listItem}>
                        To protect our rights, property, or safety, or the rights, property, or safety of our users
                    </li>
                    <li className={styles.listItem}>
                        To service providers who perform functions on our behalf (e.g., SMS delivery services)
                    </li>
                </ul>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Third-Party Services</h2>
                <p className={styles.paragraph}>
                    Our service integrates with Microsoft Azure and Office 365. Your use of these services
                    is governed by Microsoft&apos;s privacy policies. We also use Azure Communication Services
                    for SMS delivery.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Children&apos;s Privacy</h2>
                <p className={styles.paragraph}>
                    Our services are not intended for minors. We do not knowingly collect personal information
                    from children under the age of 18. If we become aware that we have collected personal
                    information from a minor, we will take steps to remove such information.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Security Measures</h2>
                <p className={styles.paragraph}>
                    We implement appropriate technical and organizational measures to protect information,
                    including encryption of data in transit and at rest. However, no method of transmission
                    over the internet or electronic storage is 100% secure, and we cannot guarantee absolute
                    security.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Changes to This Privacy Policy</h2>
                <p className={styles.paragraph}>
                    We may update this Privacy Policy from time to time. We will notify you of any changes
                    by posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date.
                    Your continued use of our services after any modification constitutes acceptance of the
                    updated Privacy Policy.
                </p>
            </div>

            <div className={styles.contact}>
                <h2 className={styles.sectionTitle}>Contact Us</h2>
                <p className={styles.paragraph}>
                    If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <p className={styles.paragraph}>
                    <strong>MacGregor Consulting LLC</strong><br />
                    Email: connect@macgregor.consulting
                </p>
            </div>
            </div>
        </div>
    );
}
